import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "../store/authStore";
import { getERPClient } from "@/lib/erp/client";
import { logger } from "@/lib/utils/logger";
import { getQueryOptions } from "@/lib/config/queryConfig";

interface User {
  name: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user_image?: string;
}

/**
 * Hook to get the current logged-in user from Frappe
 * Mode-aware: works in both direct (session) and control_plane (JWT) modes
 */
export function useCurrentUser() {
  const username = useAuthStore((state) => state.username);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: ["currentUser", username],
    queryFn: async (): Promise<User | null> => {
      if (!isAuthenticated || !username) {
        return null;
      }

      try {
        // Try to get full user details from Frappe/Tenant Server
        const client = getERPClient();
        const userDetails = await client.get<User>({
          doctype: "User",
          name: username,
        });

        return {
          name: username,
          full_name: userDetails.full_name || username,
          first_name: userDetails.first_name,
          last_name: userDetails.last_name,
          email: userDetails.email,
          user_image: userDetails.user_image,
        };
      } catch (error) {
        // If we can't get full details, just return the username from auth store
        logger.warn("Could not fetch full user details, using username:", error);
        return {
          name: username,
          full_name: username,
        };
      }
    },
    enabled: isAuthenticated && !!username,
    ...getQueryOptions("detail"),
  });
}

